import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Group } from '@shared/welibrary-graphql/types';

import LottieLoading from '@web/ui/components/generic/loading/LottieLoading';
import LottieAnimation from '@web/ui/components/generic/loading/lotties/circle-spinner.json';
import Check from '@dsc/svgs/Check';
import Picture from '@components/generic/Picture';
import Plus from '@dsc/svgs/FullSizePlus';
import Clock from '@dsc/svgs/Clock2';
import SimpleLock from '@dsc/svgs/SimpleLock';

import {
    getBackgroundImageSetForUrl,
    getRandomDefaultCoverImage,
} from '@core/utilities/constants/defaults';

import GROUP_ACTIONS from '@core/utilities/constants/group';

type GroupSelectItemProps = {
    groupId: string;
    group?: Group;
    loading?: boolean;
    membershipGroupId: string;
    skipApproval?: boolean;
    onToggle: (groupId: string, isMember: boolean) => void;
};

const GroupSelectItem: React.FC<GroupSelectItemProps> = ({
    groupId,
    group: _group,
    loading: _loading = false,
    membershipGroupId,
    skipApproval = false,
    onToggle,
}) => {
    const { t } = useTranslation();

    const [awaitingApproval, setAwaitingApproval] = useState(
        _group?.groupActionsAllowed?.includes(GROUP_ACTIONS.GROUP_CANCEL_REQUEST_JOIN)
    );
    const [requiresApproval, setRequiresApproval] = useState(
        !skipApproval && _group?.groupActionsAllowed?.includes(GROUP_ACTIONS.GROUP_REQUEST_JOIN)
    );

    const [joined, setJoined] = useState(_group?.currentUserIsMember || _group?.currentUserIsAdmin);

    useEffect(() => {
        if (_group) {
            setAwaitingApproval(
                _group.groupActionsAllowed?.includes(GROUP_ACTIONS.GROUP_CANCEL_REQUEST_JOIN)
            );
            setRequiresApproval(
                !skipApproval &&
                    _group.groupActionsAllowed?.includes(GROUP_ACTIONS.GROUP_REQUEST_JOIN)
            );
            setJoined(_group.currentUserIsMember || _group.currentUserIsAdmin);
        }
    }, [_group, skipApproval]);

    const [groupThumbSrc] = getBackgroundImageSetForUrl(
        _group?.profile?.picture || getRandomDefaultCoverImage(_group?.profile?.full_name)
    );

    const handleClick = () => {
        onToggle(groupId, joined);
    };

    const getButtonIcon = () => {
        if (joined) return <Check />;
        if (awaitingApproval) return <Clock className="h-full w-full" />;
        return <Plus strokeWidth="3" />;
    };

    const buttonIcon = getButtonIcon();

    return (
        <li className="relative !list-none pr-[10px] pt-[9px]">
            <section
                className={`flex flex-col gap-[10px] rounded-[16px] border-2 bg-white p-[10px] shadow-[0_0_4px_0_rgba(0,0,0,0.25)] transition-all ${
                    joined ? 'border-[var(--color-wl-primary)]' : 'border-white'
                }`}
            >
                <header className="relative flex items-center justify-center">
                    {_loading ? (
                        <LottieLoading height={78} width={95} lottieData={LottieAnimation} />
                    ) : (
                        <Picture
                            disableLink
                            className="!m-0 max-h-[120px] flex-shrink-0 overflow-hidden rounded-[10px]"
                            url={groupThumbSrc}
                        />
                    )}
                    <section className="absolute flex max-h-full min-w-0 max-w-[calc(100%-24px)] flex-col items-center justify-center rounded-[10px] bg-[rgba(255,255,255,0.5)] p-2 backdrop-blur-sm">
                        {_loading ? (
                            <>
                                <div
                                    role="presentation"
                                    className="animate-loading mb-2 h-4 w-full bg-gray-300"
                                />
                                <div
                                    role="presentation"
                                    className="animate-loading mb-2 h-3 w-full bg-gray-300"
                                />
                                <div
                                    role="presentation"
                                    className="animate-loading h-3 w-full bg-gray-300"
                                />
                            </>
                        ) : (
                            <>
                                <h3 className="font-poppins text-center text-base font-bold text-[var(--color-grayscale-title-active)]">
                                    {_group?.profile?.full_name}
                                </h3>
                                <span className="font-poppins line-clamp-2 overflow-hidden text-ellipsis break-words text-xs font-normal text-[var(--color-grayscale-title-active)]">
                                    {_group?.profile?.short_bio}
                                </span>
                            </>
                        )}
                    </section>
                </header>

                {!joined && (requiresApproval || awaitingApproval) && (
                    <section className="flex items-center gap-1">
                        <SimpleLock className="h-4 w-4" />
                        <span>
                            {awaitingApproval
                                ? t('common:awaiting_approval', 'Awaiting Approval')
                                : t('commn:requires_approval', 'Requires Approval')}
                        </span>
                    </section>
                )}
            </section>

            <button
                type="button"
                onClick={handleClick}
                className={`absolute right-0 top-0 flex h-[34px] w-[34px] items-center justify-center rounded-full p-[7px] transition-all ${
                    joined
                        ? 'bg-[var(--color-wl-primary)] text-white'
                        : 'border-2 border-[var(--color-wl-primary)] bg-white text-[var(--color-wl-primary)]'
                } ${_loading ? 'rotate-180' : ''}`}
            >
                {_loading ? (
                    <LottieLoading height={20} width={20} lottieData={LottieAnimation} />
                ) : (
                    buttonIcon
                )}
            </button>
        </li>
    );
};

export default GroupSelectItem;
